/**
 * Consultas e gravações de orçamentos pelo TanStack Query.
 *
 * Papel: aqui ficam `useOrcamentos` e `useFotoDoItem`, que mantêm em cache o que
 * a tela lê, e as duas mutações que gravam e mandam a listagem se atualizar.
 *
 * Como usar: toda chave deste módulo começa por `CHAVE_ORCAMENTOS`. A foto tem
 * chave própria por item, e `enabled` desligado até alguém abri-la — sem isso,
 * montar a tabela dispararia uma requisição de imagem por linha.
 *
 * `staleTime` da foto é longo porque a imagem de um item não muda sozinha: uma
 * vez carregada, reabrir a mesma foto não custa nova requisição.
 */
import { type NovoOrcamento } from '@concierge/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  atualizarOrcamento,
  buscarFotoDoItem,
  buscarOrcamentos,
  criarOrcamento,
  excluirOrcamento,
} from '../api/orcamentos';

/** Prefixo de todas as chaves de cache deste módulo. */
export const CHAVE_ORCAMENTOS = ['orcamentos'] as const;

/** Chave da lista de orçamentos. */
export const CHAVE_LISTA = [...CHAVE_ORCAMENTOS, 'lista'] as const;

/** Quanto tempo a imagem de um item permanece fresca no cache. */
const VALIDADE_DA_FOTO_EM_MS = 30 * 60 * 1000;

/**
 * Lê os orçamentos.
 *
 * @returns Resultado da consulta, do orçamento mais recente para o mais antigo.
 */
export function useOrcamentos() {
  return useQuery({
    queryKey: CHAVE_LISTA,
    queryFn: ({ signal }) => buscarOrcamentos(signal),
  });
}

/**
 * Lê a imagem de um item.
 *
 * @param itemId - O item cuja imagem se quer, ou `null` para não pedir nenhuma.
 * @returns Resultado da consulta, com `foto` nula quando o item não tem imagem.
 */
export function useFotoDoItem(itemId: string | null) {
  return useQuery({
    queryKey: [...CHAVE_ORCAMENTOS, 'foto', itemId],
    queryFn: ({ signal }) => buscarFotoDoItem(itemId ?? '', signal),
    enabled: itemId !== null,
    staleTime: VALIDADE_DA_FOTO_EM_MS,
  });
}

/** Variáveis da mutação que cria ou altera conforme venha identificador. */
interface SalvarOrcamentoVariaveis {
  /** Identificador do orçamento a alterar, ou `null` para criar um novo. */
  id: string | null;
  dados: NovoOrcamento;
}

/**
 * Cria ou altera um orçamento, conforme venha identificador.
 *
 * @returns A mutação, que recebe `id` e os dados.
 */
export function useSalvarOrcamento() {
  const clienteDeConsultas = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dados }: SalvarOrcamentoVariaveis) =>
      id === null ? criarOrcamento(dados) : atualizarOrcamento(id, dados),
    onSuccess: () => clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_LISTA }),
  });
}

/**
 * Apaga um orçamento.
 *
 * Invalida o módulo inteiro, e não só a lista: as fotos dos itens apagados
 * continuariam em cache, e reabrir um item recriado na mesma posição mostraria a
 * imagem do anterior.
 *
 * @returns A mutação, que recebe o identificador.
 */
export function useExcluirOrcamento() {
  const clienteDeConsultas = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => excluirOrcamento(id),
    onSuccess: () => clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_ORCAMENTOS }),
  });
}
